import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status }) {
  const map = {
    new: { bg: "#4c1d95", color: "#fff", label: "NEW" },
    handled: { bg: "#065f46", color: "#fff", label: "HANDLED" },
    archived: { bg: "#374151", color: "#d1d5db", label: "ARCHIVED" },
  };
  const s = map[status] || map.new;
  return (
    <span style={{
      background: s.bg, color: s.color, padding: "2px 8px", borderRadius: 999,
      fontSize: 10, fontWeight: 700, letterSpacing: "0.05em"
    }}>{s.label}</span>
  );
}

export default function AdminSubmissions() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, new: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = async (status = null) => {
    setLoading(true);
    try {
      const params = status && status !== "all" ? { status } : {};
      const r = await axios.get(`${API}/contact/submissions`, { params, withCredentials: true });
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filter);
  }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contact/submission/${id}`, { status }, { withCredentials: true });
      load(filter);
    } catch (e) { /* ignore */ }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
      });
    } catch { return iso; }
  };

  return (
    <div className="admin-shell">
      <div className="admin-topbar">
        <h1>
          <Link to="/admin" style={{ color: "#fff", textDecoration: "none" }}>
            <span className="dot" /> Dosmass CMS
          </Link>
          <span style={{ fontWeight: 400, color: "#a0a0a8", marginLeft: 8, fontSize: 14 }}>/ submissions</span>
        </h1>
        <div className="right">
          <span style={{ color: "#a0a0a8", fontSize: 13 }}>{user?.email}</span>
          <button className="btn danger" onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="logout-btn">Logout</button>
        </div>
      </div>

      <div className="admin-content">
        <div className="editor-toolbar">
          <Link to="/admin" className="btn secondary">← Back</Link>
          <div className="tab-group">
            {["all", "new", "handled", "archived"].map((s) => (
              <button key={s} className={filter === s ? "active" : ""} onClick={() => setFilter(s)} data-testid={`filter-${s}`}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <span style={{ color: "#a0a0a8", fontSize: 13, marginLeft: "auto" }} data-testid="submissions-count">
            {data.items.length} shown · {data.new} new · {data.total} total
          </span>
        </div>

        {loading ? (
          <p data-testid="loading">Loading submissions…</p>
        ) : data.items.length === 0 ? (
          <p style={{ color: "#a0a0a8" }}>No submissions found.</p>
        ) : (
          data.items.map((s) => (
            <div key={s.id} className="item-row" data-testid={`submission-${s.id}`}>
              <div className="meta">
                <span>
                  <strong style={{ color: "#fff" }}>{s.first_name} {s.last_name}</strong>
                  <span style={{ color: "#a0a0a8", marginLeft: 8 }}>{s.email} · {s.phone}</span>
                </span>
                <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "#6a6a72" }}>{formatDate(s.created_at)}</span>
                  <StatusBadge status={s.status} />
                </span>
              </div>
              <div style={{ color: "#d1d5db", fontSize: 14, marginTop: 6, whiteSpace: "pre-wrap",
                           maxHeight: expanded === s.id ? "none" : 60, overflow: "hidden", position: "relative" }}>
                {s.message}
              </div>
              {s.message.length > 160 && (
                <button
                  onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                  style={{ background: "none", border: "none", color: "#a855f7", cursor: "pointer", padding: 0, marginTop: 4, fontSize: 12 }}
                >
                  {expanded === s.id ? "Show less" : "Show more"}
                </button>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 12, fontSize: 12, color: "#6a6a72", alignItems: "center" }}>
                <span>From: {s.source_url || "—"} · Lang: {s.lang?.toUpperCase()}</span>
                <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  {s.status !== "handled" && (
                    <button className="btn secondary" style={{ padding: "6px 12px", fontSize: 12 }}
                      onClick={() => updateStatus(s.id, "handled")} data-testid={`mark-handled-${s.id}`}>
                      Mark handled
                    </button>
                  )}
                  {s.status !== "archived" && (
                    <button className="btn secondary" style={{ padding: "6px 12px", fontSize: 12 }}
                      onClick={() => updateStatus(s.id, "archived")} data-testid={`archive-${s.id}`}>
                      Archive
                    </button>
                  )}
                  {s.status !== "new" && (
                    <button className="btn secondary" style={{ padding: "6px 12px", fontSize: 12 }}
                      onClick={() => updateStatus(s.id, "new")}>
                      Reopen
                    </button>
                  )}
                  <a href={`mailto:${s.email}?subject=Re: Tu mensaje en Dosmass`} className="btn" style={{ padding: "6px 12px", fontSize: 12 }}>
                    Reply →
                  </a>
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
